import { localSaveFailed, localSaveSucceeded, serializeLocalWrite } from './localSaveState'
import * as Y from 'yjs'
import { readPageDraft, discardPageDraft } from './pageDraftJournal'
import Dexie from 'dexie'
import { db, type StoredYjsOutboxEntry, type YjsServerCursor } from './webStorage'

const EMERGENCY_PREFIX = 'ink-yjs-emergency:'

export interface PendingYjsUpdate {
  id: string
  update: Uint8Array
  batchId?: string
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 8192
  for (let i = 0; i < bytes.byteLength; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/** Synchronous last-resort journal for a tab closed before IndexedDB commits. */
export function writeEmergencyYjsState(docId: string, updateId: string, state: Uint8Array): void {
  try {
    localStorage.setItem(`${EMERGENCY_PREFIX}${docId}`, JSON.stringify({
      updateId,
      state: bytesToBase64(state),
    }))
  } catch (err) {
    console.warn('[YjsLocalPersistence] Emergency journal unavailable:', err)
  }
}

function readEmergencyYjsState(docId: string): Uint8Array | null {
  try {
    const raw = localStorage.getItem(`${EMERGENCY_PREFIX}${docId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: string }
    return parsed.state ? base64ToBytes(parsed.state) : null
  } catch (err) {
    console.warn('[YjsLocalPersistence] Emergency journal recovery failed:', err)
    return null
  }
}

export function restoreEmergencyYjsState(docId: string, ydoc: Y.Doc): boolean {
  const state = readEmergencyYjsState(docId)
  if (!state) return false
  try {
    Y.applyUpdate(ydoc, state, 'local-cache')
    return true
  } catch (err) {
    console.warn('[YjsLocalPersistence] Emergency journal recovery failed:', err)
    return false
  }
}

export function clearEmergencyYjsState(docId: string, updateId: string): void {
  try {
    const key = `${EMERGENCY_PREFIX}${docId}`
    const raw = localStorage.getItem(key)
    if (!raw) return
    const parsed = JSON.parse(raw) as { updateId?: string }
    if (parsed.updateId === updateId) localStorage.removeItem(key)
  } catch {}
}

/** Detects an encrypted local replica without needing its DEK. */
export async function hasStoredYjsState(docId: string): Promise<boolean> {
  try {
    if (localStorage.getItem(`${EMERGENCY_PREFIX}${docId}`)) return true
  } catch {}
  const [cache, outboxCount] = await Promise.all([
    db.yjsCache.get(docId),
    db.yjsOutbox.where('[docId+createdAt]').between(
      [docId, Dexie.minKey],
      [docId, Dexie.maxKey],
    ).count(),
  ])
  return Boolean(cache) || outboxCount > 0
}

async function encryptBytes(bytes: Uint8Array, key: CryptoKey) {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    bytes as BufferSource,
  )
  return { iv, ciphertext: new Uint8Array(ciphertext) }
}

async function decryptBytes(iv: Uint8Array, ciphertext: Uint8Array, key: CryptoKey) {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    ciphertext as BufferSource,
  )
  return new Uint8Array(plaintext)
}

export const YJS_OUTBOX_UPDATED_EVENT = 'ink-yjs-outbox-updated'

export async function persistLocalYjsUpdate(
  docId: string,
  id: string,
  update: Uint8Array,
  key: CryptoKey,
  epoch: number,
): Promise<void> {
  const entry = await encryptBytes(update, key)
  const now = Date.now()
  await db.yjsOutbox.put({
    id,
    docId,
    epoch,
    iv: entry.iv,
    ciphertext: entry.ciphertext,
    createdAt: now,
  })
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(YJS_OUTBOX_UPDATED_EVENT))
}

/**
 * `serverCursor` must describe a log position whose updates are all contained
 * in `fullState`. Callers capture both in the same synchronous step.
 */
export async function persistLocalYjsCache(
  docId: string,
  fullState: Uint8Array,
  key: CryptoKey,
  serverCursor?: YjsServerCursor | null,
): Promise<void> {
  await serializeLocalWrite(`yjs-cache:${docId}`, async () => {
    // A retry from a page that has closed must not replace a newer cache.
    const previous = await db.yjsCache.get(docId)
    const merged = previous ? Y.mergeUpdates([await decryptBytes(previous.iv, previous.ciphertext, key), fullState]) : fullState
    const cache = await encryptBytes(merged, key)
    let cursor = serverCursor
    if (previous?.serverCursor) {
      if (!cursor) cursor = previous.serverCursor
      else if (cursor.logId === previous.serverCursor.logId) cursor = { ...cursor, seq: Math.max(cursor.seq, previous.serverCursor.seq) }
      else cursor = null // Different generations: request a complete catch-up.
    }
    const now = Date.now()
    await db.yjsCache.put({
      docId, iv: cache.iv, ciphertext: cache.ciphertext, seq: now, updatedAt: now,
      ...(cursor ? { serverCursor: { ...cursor } } : {}),
    })
  })
}

/** Applies the compacted cache and returns the cloud cursor stored with it. */
async function restoreLocalYjsCache(
  docId: string,
  ydoc: Y.Doc,
  key: CryptoKey,
): Promise<{ restored: boolean; serverCursor: YjsServerCursor | null }> {
  const cached = await db.yjsCache.get(docId)
  if (!cached) return { restored: false, serverCursor: null }
  const state = await decryptBytes(cached.iv, cached.ciphertext, key)
  Y.applyUpdate(ydoc, state, 'local-cache')
  return { restored: true, serverCursor: cached.serverCursor ?? null }
}

export async function restoreLocalYjsState(
  docId: string,
  ydoc: Y.Doc,
  key: CryptoKey,
): Promise<boolean> {
  return (await restoreLocalYjsCache(docId, ydoc, key)).restored
}

export async function loadLocalYjsOutbox(
  docId: string,
  key: CryptoKey,
  strict = false,
): Promise<Array<{ entry: StoredYjsOutboxEntry; update: Uint8Array }>> {
  const entries = await db.yjsOutbox.where('[docId+createdAt]').between(
    [docId, Dexie.minKey],
    [docId, Dexie.maxKey],
  ).toArray()
  const decrypted = await Promise.all(entries.map(async (entry) => {
    try {
      return { entry, update: await decryptBytes(entry.iv, entry.ciphertext, key) }
    } catch (err) {
      // Keep the row. It may become decryptable again after a key refresh.
      console.warn(`[YjsLocalPersistence] Could not decrypt pending update ${entry.id}:`, err)
      if (strict) throw err
      return null
    }
  }))
  const result: Array<{ entry: StoredYjsOutboxEntry; update: Uint8Array }> = []
  for (const item of decrypted) {
    if (item) result.push(item)
  }
  return result
}

export async function assignYjsBatch(entryIds: string[], batchId: string): Promise<void> {
  if (entryIds.length === 0) return
  await db.yjsOutbox.where('id').anyOf(entryIds).modify({ batchId })
}

export async function acknowledgeYjsBatch(batchId: string): Promise<void> {
  await db.yjsOutbox.where('batchId').equals(batchId).delete()
}

async function deleteYjsOutboxEntries(entryIds: string[]): Promise<void> {
  if (entryIds.length > 0) await db.yjsOutbox.bulkDelete(entryIds)
}

/**
 * Read-only restore of everything this device holds for a page (emergency
 * journal, compacted cache and unacknowledged updates) into a scratch Y.Doc.
 * Unlike LocalYjsDocument.open, nothing is subscribed or re-journaled.
 */
export async function readLocalYjsState(
  docId: string,
  ydoc: Y.Doc,
  key: CryptoKey,
): Promise<boolean> {
  const hadEmergencyState = restoreEmergencyYjsState(docId, ydoc)
  const hadCachedState = await restoreLocalYjsState(docId, ydoc, key)
  const pending = await loadLocalYjsOutbox(docId, key)
  for (const { update } of pending) Y.applyUpdate(ydoc, update, 'local-cache')
  return hadEmergencyState || hadCachedState || pending.length > 0
}

/**
 * Stores a complete initial state for a page that has never been opened, as
 * if it had been typed locally: the compacted cache makes it render at once,
 * and the unacknowledged update is delivered to the cloud replica the first
 * time the page connects.
 */
export async function seedLocalYjsState(
  docId: string,
  state: Uint8Array,
  key: CryptoKey,
  epoch: number,
): Promise<void> {
  await persistLocalYjsUpdate(docId, crypto.randomUUID(), state, key, epoch)
  await persistLocalYjsCache(docId, state, key)
}

/** Removes every local CRDT trace of a page that no longer exists. */
export async function clearLocalYjsState(docId: string): Promise<void> {
  try {
    localStorage.removeItem(`${EMERGENCY_PREFIX}${docId}`)
  } catch {}
  await Promise.all([
    db.yjsCache.delete(docId),
    db.yjsOutbox.where('[docId+createdAt]').between(
      [docId, Dexie.minKey],
      [docId, Dexie.maxKey],
    ).delete(),
  ])
  discardPageDraft(docId)
  for (const scope of ['yjs', 'cache', 'page', 'provider', 'provider-cache']) localSaveSucceeded(`${scope}:${docId}`)
}

/**
 * Network-independent lifetime for one local Y.Doc. It restores the exact CRDT
 * identity graph, journals every local edit, and maintains a compact cache even
 * when no WebSocket provider exists.
 *
 * Durability comes from the per-edit outbox, not the cache. The cache (the
 * whole document, re-encoded and re-encrypted) only makes the next open fast,
 * so it is rewritten at most every CACHE_INTERVAL_MS while there are changes,
 * and whenever the page is hidden or closed. The one rule that keeps this
 * safe: an outbox row is deleted only after a cache write that contains its
 * edit has finished, even when the cloud acknowledged the edit long before.
 */
export class LocalYjsDocument {
  private static readonly CACHE_INTERVAL_MS = 30_000
  private static readonly openDocuments = new Set<LocalYjsDocument>()
  private static readonly durabilityDocuments = new Set<LocalYjsDocument>()
  /** Unacknowledged local edits, in order. The provider sends these. */
  public readonly pendingUpdates: PendingYjsUpdate[] = []
  public readonly hadLocalState: boolean
  private persistenceChain: Promise<void> = Promise.resolve()
  private emergencyPending: PendingYjsUpdate[] = []
  /** Acknowledged by the cloud but still in the outbox until the cache covers them. */
  private acknowledgedIds = new Set<string>()
  private cacheTimer: ReturnType<typeof setTimeout> | null = null
  private subscribers = new Set<() => void>()
  private destroyed = false
  private persistenceError: unknown = null
  /**
   * Cloud log position covered by this Y.Doc. Only moved forward by the
   * provider once the matching updates have been applied, and persisted
   * together with the state it describes.
   */
  private cursor: YjsServerCursor | null
  private updateHandler: (update: Uint8Array, origin: any) => void
  private pageHideHandler: () => void
  private visibilityHandler: () => void

  private constructor(
    public readonly docId: string,
    public readonly ydoc: Y.Doc,
    private key: CryptoKey,
    private epoch: number,
    recovered: Array<{ entry: StoredYjsOutboxEntry; update: Uint8Array }>,
    hadLocalState: boolean,
    serverCursor: YjsServerCursor | null,
  ) {
    this.hadLocalState = hadLocalState
    this.cursor = serverCursor
    this.pendingUpdates.push(...recovered.map(({ entry, update }) => ({
      id: entry.id,
      update,
      batchId: entry.batchId,
    })))
    this.updateHandler = (update, origin) => {
      if (this.destroyed || origin === 'local-cache') return
      this.scheduleCachePersistence()
      if (origin === 'remote') return
      this.queueLocalUpdate(new Uint8Array(update))
    }
    this.pageHideHandler = () => { void this.persistCacheNow().catch(() => {}) }
    // Mobile systems often suspend a backgrounded app without pagehide.
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') void this.persistCacheNow().catch(() => {})
    }
    this.ydoc.on('update', this.updateHandler)
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.pageHideHandler)
      document.addEventListener('visibilitychange', this.visibilityHandler)
    }
    LocalYjsDocument.openDocuments.add(this)
    LocalYjsDocument.durabilityDocuments.add(this)
    this.scheduleCachePersistence()
  }

  /**
   * Stops journaling a page that no longer exists: closes every open document
   * for it and waits until their last writes have finished, so nothing is
   * written back after the page's local state is cleared.
   */
  public static async closeAll(docId: string): Promise<void> {
    const documents = [...LocalYjsDocument.durabilityDocuments].filter((local) => local.docId === docId)
    for (const local of documents) local.destroy()
    await Promise.all(documents.map((local) => local.whenPersisted().catch(() => {})))
    // The caller explicitly removes this page after closing it. Failed
    // in-memory updates must not be retried into a deleted page later.
    for (const local of documents) {
      local.emergencyPending = []
      local.pendingUpdates.length = 0
      LocalYjsDocument.durabilityDocuments.delete(local)
    }
  }

  public static async closeEveryDocument(): Promise<void> {
    const documents = [...LocalYjsDocument.durabilityDocuments]
    for (const local of documents) local.destroy()
    await Promise.allSettled(documents.map(local => local.whenPersisted()))
  }

  /** Pages whose edits are currently journaled and sent by a live document. */
  public static openDocIds(): Set<string> {
    return new Set([...LocalYjsDocument.openDocuments].map((local) => local.docId))
  }

  /** Writes every open page's cache now, e.g. before reading pages back from storage. */
  public static async whenAllPersisted(): Promise<void> {
    await Promise.all([...LocalYjsDocument.durabilityDocuments].map(local => local.whenPersisted()))
  }
  public static async persistAll(): Promise<void> {
    await Promise.all([...LocalYjsDocument.durabilityDocuments].map(async local => { await local.whenPersisted(); await local.persistCacheNow() }))
  }

  public static async open(
    docId: string,
    ydoc: Y.Doc,
    key: CryptoKey,
    epoch: number,
  ): Promise<LocalYjsDocument> {
    const draftState = readPageDraft(docId)?.yjsState
    const draftUpdate = draftState ? base64ToBytes(draftState) : null
    if (draftUpdate) Y.applyUpdate(ydoc, draftUpdate, 'local-cache')
    const emergencyState = readEmergencyYjsState(docId)
    const hadEmergencyState = restoreEmergencyYjsState(docId, ydoc)
    const cached = await restoreLocalYjsCache(docId, ydoc, key)
    const recovered = await loadLocalYjsOutbox(docId, key)
    for (const { update } of recovered) Y.applyUpdate(ydoc, update, 'local-cache')
    const document = new LocalYjsDocument(
      docId,
      ydoc,
      key,
      epoch,
      recovered,
      Boolean(draftUpdate) || hadEmergencyState || cached.restored || recovered.length > 0,
      cached.serverCursor,
    )
    // Edits that only reached the emergency journal never entered the outbox.
    // Journal them now so they are delivered like any other local edit (the
    // provider no longer re-uploads the whole page on every connection).
    if (hadEmergencyState && emergencyState) document.queueLocalUpdate(emergencyState)
    if (draftUpdate) document.queueLocalUpdate(draftUpdate)
    return document
  }

  public static hasAcknowledged(docId: string, id: string): boolean {
    return [...LocalYjsDocument.durabilityDocuments].some(local => local.docId === docId && local.acknowledgedIds.has(id))
  }

  /** Import journal entries committed after this replica was first opened. */
  public reconcilePendingUpdates(recovered: Array<{ entry: StoredYjsOutboxEntry; update: Uint8Array }>): void {
    if (this.destroyed) return
    const known = new Set(this.pendingUpdates.map(item => item.id))
    let added = false
    for (const { entry, update } of recovered) {
      if (known.has(entry.id) || this.acknowledgedIds.has(entry.id)) continue
      Y.applyUpdate(this.ydoc, update, 'local-cache')
      this.pendingUpdates.push({ id: entry.id, update, batchId: entry.batchId })
      known.add(entry.id)
      added = true
    }
    if (added) {
      this.scheduleCachePersistence()
      for (const subscriber of this.subscribers) subscriber()
    }
  }

  public get serverCursor(): YjsServerCursor | null {
    return this.cursor
  }

  /** Call only after every update up to `cursor.seq` has been applied to the Y.Doc. */
  public setServerCursor(cursor: YjsServerCursor | null): void {
    this.cursor = cursor ? { ...cursor } : null
  }

  public subscribe(callback: () => void): () => void {
    this.subscribers.add(callback)
    return () => this.subscribers.delete(callback)
  }

  public get localSaveStatus(): 'saving_local' | 'local_error' | 'saved_local' {
    return this.persistenceError ? 'local_error' : this.emergencyPending.length ? 'saving_local' : 'saved_local'
  }
  private notify(): void { for (const subscriber of this.subscribers) subscriber() }
  public async whenPersisted(): Promise<void> {
    await this.persistenceChain.catch(() => {})
    if (this.emergencyPending.length) await this.retryPersistence()
    if (this.emergencyPending.length) throw this.persistenceError ?? new Error('Local edits have not been saved')
  }
  private async persistPending(): Promise<void> {
    const latest = this.emergencyPending[this.emergencyPending.length - 1]
    for (const item of [...this.emergencyPending]) {
      await persistLocalYjsUpdate(this.docId, item.id, item.update, this.key, this.epoch)
      this.emergencyPending = this.emergencyPending.filter(pending => pending.id !== item.id)
    }
    this.persistenceError = null
    if (this.emergencyPending.length === 0 && latest) clearEmergencyYjsState(this.docId, latest.id)
    if (this.emergencyPending.length === 0) {
      localSaveSucceeded(`yjs:${this.docId}`)
      if (this.destroyed) LocalYjsDocument.durabilityDocuments.delete(this)
    }
    this.notify()
  }
  private async retryPersistence(): Promise<void> {
    const task = this.persistenceChain.catch(() => {}).then(() => this.persistPending())
    this.persistenceChain = task.catch(cause => { this.reportPersistenceFailure(cause) })
    await task
  }
  private reportPersistenceFailure(cause: unknown): void {
    this.persistenceError = cause
    localSaveFailed(`yjs:${this.docId}`, cause, () => this.retryPersistence())
    this.notify()
  }

  public async assignBatch(entryIds: string[], batchId: string): Promise<void> {
    await this.whenPersisted()
    await assignYjsBatch(entryIds, batchId)
    const selected = new Set(entryIds)
    for (const item of this.pendingUpdates) {
      if (selected.has(item.id)) item.batchId = batchId
    }
  }

  /**
   * Stops sending a batch the cloud has stored. Its outbox rows stay until the
   * next cache write contains them; if the app stops first, they are resent
   * on the next open, which the server's batch id dedup absorbs.
   */
  public async acknowledgeBatch(batchId: string): Promise<void> {
    for (let i = this.pendingUpdates.length - 1; i >= 0; i--) {
      if (this.pendingUpdates[i].batchId === batchId) {
        this.acknowledgedIds.add(this.pendingUpdates[i].id)
        this.pendingUpdates.splice(i, 1)
      }
    }
    this.scheduleCachePersistence()
  }

  public setKey(key: CryptoKey, epoch?: number): void {
    this.key = key
    if (epoch !== undefined) this.epoch = epoch
  }

  public async persistCacheNow(): Promise<void> {
    if (this.destroyed) return
    if (this.cacheTimer) {
      clearTimeout(this.cacheTimer)
      this.cacheTimer = null
    }
    // Captured in one synchronous step with the state: every acknowledged edit
    // is a local edit already in this Y.Doc, so this snapshot contains it.
    const snapshot = Y.encodeStateAsUpdate(this.ydoc)
    const cursor = this.cursor
    const covered = [...this.acknowledgedIds]
    const key = this.key
    const persistSnapshot = async () => {
      await persistLocalYjsCache(this.docId, snapshot, key, cursor)
      await deleteYjsOutboxEntries(covered)
      for (const id of covered) this.acknowledgedIds.delete(id)
      localSaveSucceeded(`cache:${this.docId}`)
    }
    const retry = async () => {
      const task = this.persistenceChain.catch(() => {}).then(persistSnapshot)
      this.persistenceChain = task.catch(cause => { localSaveFailed(`cache:${this.docId}`, cause, retry) })
      await task
    }
    const task = this.persistenceChain.catch(() => {}).then(persistSnapshot)
    this.persistenceChain = task.catch(cause => { localSaveFailed(`cache:${this.docId}`, cause, retry) })
    await task
  }

  /**
   * Asks for a cache write within CACHE_INTERVAL_MS. The delay runs from the
   * first change rather than the last, so steady writing is still cached.
   */
  public scheduleCachePersistence(): void {
    if (this.destroyed || this.cacheTimer) return
    this.cacheTimer = setTimeout(() => {
      this.cacheTimer = null
      void this.persistCacheNow().catch((err) => {
        console.warn(`[YjsLocalPersistence] Failed to compact local state for ${this.docId}:`, err)
      })
    }, LocalYjsDocument.CACHE_INTERVAL_MS)
  }

  private queueLocalUpdate(update: Uint8Array): void {
    const item: PendingYjsUpdate = { id: crypto.randomUUID(), update }
    this.pendingUpdates.push(item)
    this.emergencyPending.push(item)
    writeEmergencyYjsState(
      this.docId,
      item.id,
      Y.mergeUpdates(this.emergencyPending.map((pending) => pending.update)),
    )
    this.persistenceChain = this.persistenceChain.catch(() => {}).then(async () => {
      await this.persistPending()
      if (this.emergencyPending.length === 0) clearEmergencyYjsState(this.docId, item.id)
    }).catch(cause => { this.reportPersistenceFailure(cause) })
    this.notify()
  }

  public destroy(): void {
    if (this.destroyed) return
    if (this.cacheTimer) {
      clearTimeout(this.cacheTimer)
      this.cacheTimer = null
    }
    void this.persistCacheNow().catch(() => {})
    this.destroyed = true
    LocalYjsDocument.openDocuments.delete(this)
    void this.whenPersisted().then(() => LocalYjsDocument.durabilityDocuments.delete(this)).catch(() => {})
    this.ydoc.off('update', this.updateHandler)
    this.subscribers.clear()
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pageHideHandler)
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
  }
}

/**
 * Pages with unacknowledged journal entries, including warm local documents.
 * Background delivery sends these without opening an editor. Acknowledged
 * entries awaiting durable cache compaction are excluded from this backlog.
 */
export async function listDormantPagesWithEdits(): Promise<string[]> {
  return (await summarizeDormantPageEdits()).map(page => page.docId)
}

/** Per page: how many unacknowledged edits wait, and since when. */
export async function summarizeDormantPageEdits(): Promise<Array<{ docId: string; edits: number; since: number }>> {
  const byPage = new Map<string, { docId: string; edits: number; since: number }>()
  await db.yjsOutbox.each((entry) => {
    if (LocalYjsDocument.hasAcknowledged(entry.docId, entry.id)) return
    const page = byPage.get(entry.docId)
    if (page) {
      page.edits++
      page.since = Math.min(page.since, entry.createdAt)
    } else {
      byPage.set(entry.docId, { docId: entry.docId, edits: 1, since: entry.createdAt })
    }
  })
  return [...byPage.values()]
}
